import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaEye, FaFileSignature, FaTasks } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import StudentsFormVerification from "./StudentFormVerification";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { config } from "../CommonUtils/CommonURLS";
import { failureAlert, successAlert2 } from "../CommonUtils/SweetAlerts";
import StudentFormReceiving from "./StudentFormReceiving";
import StudentPreview from "../Student Services/StuServices/StudentPreview";
import CommonReactTable from "../CommonUtils/CommonReactTable";
import NocForm from "../CollegeServices/NocForm";
import RejectNoc from "../CollegeServices/RejectNoc";
import CommonCardHeader from "../CommonUtils/CommonCardHeader";
// Common Transfer Table
export default function StudentsTransferTable() {
  // Table and modal visibility state
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [showCollegeVerificationModal, setShowCollegeVerificationModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showInlineNocForm, setShowInlineNocForm] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState([]);
  const [verifiedStatus, setVerifiedStatus] = useState("");

  const [cteAdminDecisions, setCteAdminDecisions] = useState({});
  const [cteAdminPostingStudentId, setCteAdminPostingStudentId] = useState("");
  const [cteAdminRemarks, setCteAdminRemarks] = useState({});
  const [showRejectNocModal, setShowRejectNocModal] = useState(false);
  const location = useLocation();

  const sanitizeServiceName = (value) =>
    String(value || "")
      .trim()
      .replace(/\s+/g, " ");
  const normalizeServiceName = (value) => sanitizeServiceName(value).toLowerCase();
  const getServiceNameFromPath = (pathname) => {
    const route = String(pathname || "")
      .replace(/^\/+/, "")
      .toLowerCase();
    if (route === "studentverification") return "Student Verification";
    if (route === "studentformreceiving") return "Student Form Receiving";
    if (route === "studentdetails") return "Student Details";
    return "";
  };
  const readLatestServiceName = () => {
    const fromStorage = sanitizeServiceName(localStorage.getItem("ServiceName"));
    const fromPath = getServiceNameFromPath(location.pathname);
    return fromPath || fromStorage;
  };

  const [serviceName, setServiceName] = useState(() => readLatestServiceName());
  const isMountedRef = useRef(true);
  const [receivingFormData, setReceivingFormData] = useState([]);
  const lastFetchedServiceRef = useRef(null);
  const normalizedServiceName = normalizeServiceName(serviceName);
  const isStudentFormReceiving = normalizedServiceName === "student form receiving";
  const isStudentVerification = normalizedServiceName === "student verification";
  let activeFormId = "";
  if (isStudentFormReceiving) {
    activeFormId = "student-receiving-form";
  } else if (isStudentVerification) {
    activeFormId = "student-verification-form";
  }

  // Header labels are derived from selected workflow
  let workflowTitle = "Student Transfer Workflow";
  let workflowSubtitle = "Review Applications";
  if (isStudentFormReceiving) {
    workflowTitle = "Student Transfer To College Verification";
    workflowSubtitle = "Review Received Applications";
  } else if (isStudentVerification) {
    workflowTitle = "Student Transfer From College Verification";
    workflowSubtitle = "Review Applications for College Transfer";
  }

  let collegeColumnHeader = "College";
  if (isStudentVerification) {
    collegeColumnHeader = "Transfer College Name";
  } else if (isStudentFormReceiving) {
    collegeColumnHeader = "From College Name";
  }

  const emptyRowColSpan = isStudentFormReceiving ? "10" : "9";

  const getCollegeDisplayName = (student) => {
    if (isStudentVerification) {
      return student?.trans_collname || student?.trans_coll_code ? `${student?.trans_collname || ""} - ${student?.trans_coll_code || ""}` : "-";
    }
    if (isStudentFormReceiving) {
      return student?.prsent_collname || student?.present_coll_code ? `${student?.prsent_collname || ""} - ${student?.present_coll_code || ""}` : "-";
    }
    return "-";
  };

  const shouldShowMainSubmitButton = Boolean(activeFormId && !(verificationDetails?.length > 0 && isStudentVerification));
  const userId = sessionStorage.getItem("user_id");
  // Role check drives endpoint and action controls
  const isCteAdmin = userId === "cteadmin";
  const handleCteDecisionChange = (student, decision) => {
    const studentKey = student?.student_reg_no;
    setCteAdminDecisions((prev) => ({ ...prev, [studentKey]: decision }));
  };

  const handleCteDecisionSubmit = async (student) => {
    const studentKey = student?.student_reg_no;
    const decision = cteAdminDecisions[studentKey];
    if (!decision) {
      failureAlert("Please select Approve or Reject.");
      return;
    }
    // Check remarks for Reject decision
    if (decision === "Rejected" && !cteAdminRemarks[studentKey]) {
      failureAlert("Please provide remarks for rejection.");
      return;
    }
    const payload = {
      stuId: student?.student_reg_no,
      ctAdminStatus: decision,
    };
    // Add remarks only if decision is Rejected
    if (decision === "Rejected") {
      payload.remarks = cteAdminRemarks[studentKey];
    }
    setCteAdminPostingStudentId(studentKey);
    try {
      const res = await CommonAxiosPost(`${config.url.STU_URL}insertctadminstatus`, payload);
      if (res?.data?.status === true || res?.data?.scode === "01") {
        successAlert2(res?.data?.message || res?.data?.sdesc || `${decision} submitted successfully`);
        setCteAdminDecisions((prev) => ({ ...prev, [studentKey]: "" }));
        fetchStudents();
      } else {
        failureAlert(res?.data?.message || res?.data?.sdesc || "Submission failed");
      }
    } catch (error) {
      console.error("Error submitting decision:", error);
      failureAlert("Something went wrong while submitting decision.");
    } finally {
      setCteAdminPostingStudentId("");
    }
  };
  // Define table columns for CommonReactTable
  const columns = [
    {
      id: "slNo",
      header: "S.No",
      cell: ({ row }) => row.index + 1,
    },
    {
      id: "student_reg_no",
      header: "Student ID",
      accessorKey: "student_reg_no",
      meta: { style: { textAlign: "center" } },
    },
    {
      id: "student_name",
      header: "Student Name",
      accessorKey: "student_name",
      cell: ({ row }) => <div style={{ textAlign: "left" }}>{row.original.student_name}</div>,
    },
    {
      id: "course_name",
      header: "Course/Year/SEM",
      accessorKey: "course_name",
      cell: ({ row }) => <div style={{ textAlign: "left" }}>{row.original.course_name}</div>,
    },
    {
      id: "college",
      header: isCteAdmin ? "From College" : collegeColumnHeader,
      cell: ({ row }) => {
        if (isCteAdmin) {
          return (
            <div style={{ textAlign: "left" }}>
              {row.original?.prsent_collname || row.original?.present_coll_code ? `${row.original?.prsent_collname || ""} - ${row.original?.present_coll_code || ""}` : "-"}
            </div>
          );
        }

        return <div style={{ textAlign: "left" }}>{getCollegeDisplayName(row.original)}</div>;
      },
    },
    ...(isCteAdmin
      ? [
        {
          id: "transfer_college",
          header: "To College",
          cell: ({ row }) => (
            <div style={{ textAlign: "left" }}>
              {row.original?.trans_collname || row.original?.trans_coll_code ? `${row.original?.trans_collname || ""} - ${row.original?.trans_coll_code || ""}` : "-"}
            </div>
          ),
        },
      ]
      : []),
    {
      id: "transfer_reason",
      header: "Reason for Transfer",
      accessorKey: "transfer_reason",
      cell: ({ row }) => <div style={{ textAlign: "left" }}>{row.original.transfer_reason}</div>,
    },
    {
      id: "student_particulars",
      header: "Student Preview",
      cell: ({ row }) => (
        <Button variant="secondary" size="sm" onClick={() => handlePreviewClick(row.original)}>
          <FaEye />
        </Button>
      ),
      meta: { style: { textAlign: "center" } },
    },
  ];
  // Add verification column only for Form Receiving workflow
  if (isStudentFormReceiving || isCteAdmin) {
    columns.push({
      id: "college_verification",
      header: "Verified by Current College(From College)",
      cell: ({ row }) => (
        <Button variant="primary" size="sm" onClick={() => handleCollegeVerificationClick(row.original, "verification")}>
          {" "}
          <FaEye />{" "}
        </Button>
      ),
      meta: { style: { textAlign: "center" } },
    });
  }

  if (isCteAdmin) {
    columns.push({
      id: "college_receiving",
      header: "Received by New College(To College)",
      cell: ({ row }) => (
        <Button variant="primary" size="sm" onClick={() => handleCollegeVerificationClick(row.original, "Receiving")}>
          {" "}
          <FaEye />{" "}
        </Button>
      ),
      meta: { style: { textAlign: "center" } },
    });
  }
  if (isStudentFormReceiving || isStudentVerification) {
    columns.push({
      id: "noc_action",
      header: "NOC Action",
      cell: ({ row }) => {
        const nocStatus = normalizeNocStatus(row.original.noc_status);

        if (!nocStatus) {
          return (
            <select
              className="form-select form-select-sm"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value === "Approved") handleNocClick(row.original);
                if (e.target.value === "Rejected") handleRejectNoc(row.original);
                e.target.value = "";
              }}
            >
              <option value="">Select</option>
              <option value="Approved">Issue NOC</option>
              <option value="Rejected">Reject NOC</option>
            </select>
          );
        }

        if (nocStatus === "approved") {
          return (
            <div className="d-flex flex-column align-items-center gap-1">
              <button className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                onClick={() => handleNocClick(row.original)}><FaEye className="me-1" size="1.3em" /> Approved
              </button>
              {/* <Button variant="success" size="sm" onClick={() => handleNocClick(row.original)} title="View Approved NOC">
                View NOC
              </Button> */}
            </div>
          );
        }

        if (nocStatus === "rejected") {
          return (
            <div className="d-flex flex-column align-items-center gap-1">
              <button className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1" onClick={() => handleRejectNoc(row.original)}>
                <FaEye className="me-1" size="1.3em" /> Rejected
              </button>
              {/* <Button variant="danger" size="sm" onClick={() => handleRejectNoc(row.original)} title="View Rejected NOC">
                View Rejection
              </Button> */}
            </div>
          );
        }

        return (
          <span className="badge bg-secondary">{row.original.noc_status || "Updated"}</span>
        );
      },
      meta: { style: { textAlign: "center" } },
    });
  }
  if (isStudentFormReceiving || isStudentVerification) {
    columns.push({
      id: "pdf_proceedings",
      header: "PDF Proceedings",
      cell: ({ row }) =>
        row.original.noc_status == null || row.original.noc_status === "" ? (
          <span className="">-</span>
        ) : (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              getGenerateData(
                row.original.student_reg_no,
                isStudentVerification ? "fromCollege" : "toCollege",
                row.original.noc_status
              )
            }
          >
            <FaFileSignature className="me-1" />PDF
          </Button>
        ),

      meta: { style: { textAlign: "center" } },
    });
  }
  // Add status column
  // if (!isCteAdmin) {
  //   columns.push({
  //     id: "status",
  //     header: "Status",
  //     cell: ({ row }) => getStatusBadge(row.original.status),
  //     meta: { style: { textAlign: "center" } },
  //   });
  // }

  // Add action column
  columns.push({
    id: "action",
    header: "Action",
    cell: ({ row }) =>
      isCteAdmin ? (
        (() => {
          const studentKey = row.original.student_reg_no;
          const selectedDecision = cteAdminDecisions[studentKey] || "";
          const isReject = selectedDecision === "Rejected";
          const ct_admin_status = row.original.ct_admin_status || "";
          return (
            <>
              {ct_admin_status === "Approved" || ct_admin_status === "Rejected" ? (
                <span className={`badge ${ct_admin_status === "Approved" ? "bg-success" : "bg-danger"}`}>{ct_admin_status}</span>
              ) : (
                <div className={`d-flex ${isReject ? "flex-column" : ""} gap-2 align-items-center justify-content-center`} style={{ minWidth: isReject ? "260px" : "220px" }}>
                  <Form.Select value={selectedDecision} onChange={(e) => handleCteDecisionChange(row.original, e.target.value)} size="sm" style={!isReject ? { minWidth: "130px" } : {}}>
                    <option value="">Select</option>
                    <option value="Approved">Approve</option>
                    <option value="Rejected">Reject</option>
                  </Form.Select>
                  {isReject && (
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter rejection remarks..."
                      value={cteAdminRemarks[studentKey] || ""}
                      onChange={(e) => setCteAdminRemarks((prev) => ({ ...prev, [studentKey]: e.target.value }))}
                      size="sm"
                    />
                  )}
                  <Button variant="primary" size="sm" onClick={() => handleCteDecisionSubmit(row.original)} style={isReject ? { width: "100%" } : {}}>
                    {" "}
                    Submit{" "}
                  </Button>
                </div>
              )}
            </>
          );
        })()
      ) : (
        (() => {
          const nocStatus = normalizeNocStatus(row.original?.noc_status);
          const shouldShowFillNocForm = !nocStatus;

          return shouldShowFillNocForm ? (
            <span className="text-danger small">Available after NOC action</span>
          ) : (
            <Button variant="info" size="sm" onClick={() => handleCheckClick(row.original)}>
              <FaEye className="me-1" /> Check
            </Button>
          );
        })()
      ),
    meta: { style: { textAlign: "center" } },
  });

  useEffect(() => {
    // Keep workflow selection in sync for route changes and storage updates.
    const syncServiceName = () => {
      if (!isMountedRef.current) return;
      const latest = readLatestServiceName();
      setServiceName((prev) => {
        if (normalizeServiceName(prev) === normalizeServiceName(latest)) {
          return prev;
        }
        return latest;
      });
    };

    syncServiceName();
    window.addEventListener("ServiceNameChanged", syncServiceName);
    window.addEventListener("storage", syncServiceName);
    window.addEventListener("popstate", syncServiceName);
    window.addEventListener("focus", syncServiceName);

    return () => {
      window.removeEventListener("ServiceNameChanged", syncServiceName);
      window.removeEventListener("storage", syncServiceName);
      window.removeEventListener("popstate", syncServiceName);
      window.removeEventListener("focus", syncServiceName);
    };
  }, [location.pathname]);
  useEffect(() => {
    // Safety guard to avoid setting state after unmount
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Prevent duplicate refetch for the same workflow value
    if (!normalizedServiceName || lastFetchedServiceRef.current === normalizedServiceName) return;
    lastFetchedServiceRef.current = normalizedServiceName;
    fetchStudents();
  }, [normalizedServiceName]);

  useEffect(() => {
    setShowInlineNocForm(false);
    setShowRejectNocModal(false);
    setShowCheckModal(false);
    setShowCollegeVerificationModal(false);
    setShowPreviewModal(false);
    setSelectedStudent(null);
    setVerificationDetails([]);
    setReceivingFormData([]);
    setVerifiedStatus("");
  }, [normalizedServiceName]);

  function getGenerateData(stuId, colltype, nocStatus) {
    let url;
    if (nocStatus === "Approved") {
      url = `${config.url.STU_URL}generate/${stuId}/${colltype}`;
    } else if (nocStatus === "Rejected") {
      url = `${config.url.STU_URL}generaterejection/${stuId}/${colltype}`;
    }

    CommonAxiosPost(url)
      .then((res) => {
        if (res?.data?.status === true || res?.data?.scode === "01") {
          const base64Pdf = res?.data?.pdfBase64;
          if (base64Pdf) {
            // Open PDF in new tab
            const pdfWindow = window.open();
            pdfWindow.document.write(`
            <iframe src="data:application/pdf;base64,${base64Pdf}" 
                    width="100%" height="100%" style="border:none;"></iframe>
          `);
          } else {
            failureAlert("No PDF data received");
          }
        } else {
          failureAlert(res?.data?.message || "NOC Status is Pending.");
        }
      })
      .catch((error) => {
        console.error("Error fetching generate data:", error);
        failureAlert("Error generating PDF");
      });
  }
  const getStatusBadge = (status) => {
    const value = String(status || "Pending").trim();
    if (value === "Approved") return <span className="badge bg-success">Approved</span>;
    if (value === "Rejected") return <span className="badge bg-danger">Rejected</span>;
    if (value === "NOC Issued") return <span className="badge bg-primary">NOC Issued</span>;
    if (value === "Pending Review") return <span className="badge bg-info">Pending Review</span>;
    return <span className="badge bg-warning text-dark">{status}</span>;
  };

  const normalizeNocStatus = (status) => String(status || "").trim().toLowerCase();

  const getCollegeNameFromItem = (item, type) => {
    if (type === "present") {
      return item?.prsent_collname || "-";
    }
    return item?.trans_collname || "-";
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      // API endpoint selection is role/workflow based
      let endpoint = "";
      if (isCteAdmin) {
        endpoint = "getdeptcollegedetails";
      } else if (isStudentFormReceiving) {
        endpoint = "gettocollegedetails";
      } else if (isStudentVerification) {
        endpoint = "getpresentcollegedetails";
      }

      if (!endpoint) {
        setStudents([]);
        return;
      }

      const response = await CommonAxiosGet(`${config.url.STU_URL}${endpoint}`);
      const rows = response?.data?.collegedetails || response?.data?.tocollegedetails || response?.data?.data || [];

      if (response?.data?.scode === "01" && Array.isArray(rows) && rows.length > 0) {
        setStudents(rows);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
      failureAlert("Failed to fetch student data.");
    } finally {
      setLoading(false);
    }
  };
  function handlefetchDetails(studentId) {
    if (!studentId) {
      setVerificationDetails([]);
      return;
    }

    const endpoint = `getStudentVerificationData?StudentId=${studentId}`;
    CommonAxiosGet(`${config.url.STU_URL}${endpoint}`)
      .then((res) => {
        if (res?.data?.status === true || res?.data?.scode === "01") {
          setVerificationDetails(res?.data?.data);
        } else {
          setVerificationDetails([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching verification details:", error);
        setVerificationDetails([]);
      });
  }

  const handlePreviewClick = (student) => {
    setSelectedStudent(student);
    setShowPreviewModal(true);
  };

  const handleCheckClick = (student) => {
    setSelectedStudent(student);
    setVerificationDetails([]);
    setShowCheckModal(true);
    handlefetchDetails(student?.student_reg_no);
    getReceivingFormData(student?.student_reg_no);
  };

  const handleCollegeVerificationClick = (student, type) => {
    setSelectedStudent(student);
    setVerificationDetails([]);
    setShowCollegeVerificationModal(true);
    setVerifiedStatus(type);
    handlefetchDetails(student?.student_reg_no);
    getReceivingFormData(student?.student_reg_no);
  };

  const handleNocClick = (student) => {
    setSelectedStudent(student);
    setShowInlineNocForm(true);
  };

  const handleBackFromNocForm = () => {
    setShowInlineNocForm(false);
    setSelectedStudent(null);
  };
  const handleRejectNoc = (student) => {
    setSelectedStudent(student);
    setShowRejectNocModal(true);
  };

  const handleBackFromRejectNocForm = () => {
    setShowRejectNocModal(false);
    setSelectedStudent(null);
  };
  if (loading && students.length === 0) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading students...</p>
      </div>
    );
  }

  function getReceivingFormData(studentRegNo) {
    let url = `${config.url.STU_URL}getReceivingWorkFlow?application_no=${studentRegNo}`;
    CommonAxiosGet(url)
      .then((res) => {
        if (res?.data?.status === true || res?.data?.scode === "01") {
          setReceivingFormData(res?.data?.data);
        } else {
          setReceivingFormData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching receiving form data:", error);
        setReceivingFormData([]);
      });
  }

  if (showInlineNocForm && selectedStudent) {
    return (
      <Container fluid className="py-4">
        <section className="workflow-section-surface">
          <CommonCardHeader
            title={`${isStudentVerification ? "NOC Form For Leaving The College" : "NOC Form For Entering The College"} - ${selectedStudent?.student_name || "Student"}`}
            onBack={handleBackFromNocForm}
          />
          <div className="workflow-section-body">
            <NocForm
              selectedStudent={selectedStudent}
              setShowCheckModal={setShowInlineNocForm}
              fetchStudents={fetchStudents}
              setSubmitting={setSubmitting}
              fromToCollege={isStudentVerification ? "fromCollege" : "toCollege"}
            />
          </div>
        </section>
      </Container>
    );
  }
  if (showRejectNocModal && selectedStudent) {
    return (
      <Container fluid className="py-4">
        <section className="workflow-section-surface">
          <CommonCardHeader
            title={`NOC Rejection Form - ${selectedStudent?.student_name || "Student"}`}
            onBack={handleBackFromRejectNocForm}
          />
          <div className="workflow-section-body">
            <RejectNoc
              selectedStudent={selectedStudent}
              setShowCheckModal={setShowRejectNocModal}
              fetchStudents={fetchStudents}
              setSubmitting={setSubmitting}
              fromToCollege={isStudentVerification ? "fromCollege" : "toCollege"}
            />
          </div>
        </section>
      </Container>
    );
  }
  return (
    <Container fluid className="py-4">
      <section className="workflow-section-surface">
        <CommonCardHeader title={workflowTitle} subtitle={workflowSubtitle} />
        <div className="workflow-section-body">
          {students.length > 0 ? (
            <CommonReactTable
              data={students}
              columns={columns}
              filename="StudentTransferApplications"
              headerName="Student Transfer Applications"
              showFooter={false}
              showPagination={false}
              showExcel={true}
              showPrint={true}
              excelFileName="Student Transfer Applications"
            />
          ) : (
            <div style={{ textAlign: "center", color: "red", fontWeight: "bold" }}>** No Records Found **</div>
          )}
        </div>
      </section>
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" backdrop="static">
        {/* Read-only student details preview */}
        <Modal.Header closeButton>
          <Modal.Title>Student Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <StudentPreview studentRegNo={selectedStudent?.student_reg_no} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showCheckModal} onHide={() => setShowCheckModal(false)} size="xl" backdrop="static">
        {/* Main decision modal for verification or receiving workflow */}
        <Modal.Header closeButton>
          <Modal.Title>Review Application - {selectedStudent?.student_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <strong>Student Id:</strong> {selectedStudent?.student_reg_no || "-"}
                </Col>
                <Col md={4}>
                  <strong>Student Name:</strong>
                  {selectedStudent?.student_name || "-"}
                </Col>
                <Col md={5}>
                  <strong>To College:</strong> {selectedStudent?.trans_collname || "-"}
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={3}>
                  <strong>Course:</strong> {selectedStudent?.course_name || "-"}
                </Col>
                <Col md={4}>
                  <strong>Reason for Transfer:</strong> {selectedStudent?.transfer_reason || "-"}
                </Col>
                {/* <Col md={5}><strong>Percentage:</strong> {selectedStudent?.percentage || selectedStudent?.marks_percentage || '-'}%</Col> */}
              </Row>
            </Card.Body>
          </Card>
          {isStudentVerification && (
            <StudentsFormVerification
              selectedStudent={selectedStudent}
              setShowCheckModal={setShowCheckModal}
              fetchStudents={fetchStudents}
              setSubmitting={setSubmitting}
              verificationDetails={verificationDetails}
            />
          )}
          {isStudentFormReceiving && <StudentFormReceiving selectedStudent={selectedStudent} setShowCheckModal={setShowCheckModal} fetchStudents={fetchStudents} setSubmitting={setSubmitting} />}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setShowCheckModal(false);
            }}
          >
            Cancel
          </Button>
          {/* {shouldShowMainSubmitButton && (
                        <Button type="submit" form={activeFormId} variant="primary" >
                            Submit
                        </Button>
                    )} */}
          {(serviceName === "Student Verification" && verificationDetails?.length > 0) || (serviceName === "Student Form Receiving" && receivingFormData?.length > 0) ? null : (
            <Button type="submit" form={activeFormId} variant="primary">
              Submit
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Modal show={showCollegeVerificationModal} onHide={() => setShowCollegeVerificationModal(false)} size="xl" backdrop="static">
        {/* Previous college verification modal */}
        <Modal.Header closeButton>
          <Modal.Title>
            {verifiedStatus === "Receiving" ? "College Receiving" : "College Verification"} - {selectedStudent?.student_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card className="mb-3 bg-light">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <strong>Student Id:</strong> {selectedStudent?.student_reg_no || "-"}
                </Col>
                <Col md={4}>
                  <strong>Student Name:</strong>
                  {selectedStudent?.student_name || "-"}
                </Col>
                <Col md={5}>
                  <strong>To College:</strong> {selectedStudent?.trans_collname || "-"}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {verifiedStatus === "verification" && (
            <StudentsFormVerification
              selectedStudent={selectedStudent}
              setShowCheckModal={setShowCollegeVerificationModal}
              fetchStudents={fetchStudents}
              setSubmitting={setSubmitting}
              verificationDetails={verificationDetails}
            />
          )}
          {verifiedStatus === "Receiving" && (
            <StudentFormReceiving selectedStudent={selectedStudent} setShowCheckModal={setShowCollegeVerificationModal} fetchStudents={fetchStudents} setSubmitting={setSubmitting} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setShowCollegeVerificationModal(false);
            }}
          >
            Cancel
          </Button>
          {verificationDetails?.length > 0 ? null : (
            <Button type="submit" form="student-verification-form" variant="primary">
              Submit
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
